import { createContext, useCallback, useEffect, useMemo, useReducer } from "react";
// utils
import localStorageAvailable from "../utils/localStorageAvailable";
//
import { useNavigate } from "react-router";
import { API_URLS } from "src/api/api-urls";
import useApiServices from "src/api/use-api-services";
import { DASHBOARD_ROOT, PATH_AFTER_LOGIN } from "src/routes/paths";
import axiosInstance from "../utils/axios";
import { ActionMapType, AuthStateType, AuthUserType, JWTContextType } from "./types";

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = "INITIAL",
  LOGIN = "LOGIN",
  REGISTER = "REGISTER",
  LOGOUT = "LOGOUT",
}

type Payload = {
  [Types.INITIAL]: {
    isLoggedIn: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  isInitialized: false,
  isLoggedIn: false,
  user: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isLoggedIn: action.payload.isLoggedIn,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isLoggedIn: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      isLoggedIn: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    localStorage.removeItem("user");
    localStorage.removeItem("fcm_token");
    localStorage.removeItem("csrf_token");
    return {
      ...state,
      isLoggedIn: false,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  // #region States and hooks
  const [state, dispatch] = useReducer(reducer, initialState);
  const storageAvailable = localStorageAvailable();
  const navigate = useNavigate();
  const { usePostApi } = useApiServices();
  // #endregion

  // #region Services
  const { mutate: mutateLogout, isLoading: isLoggingOut } = usePostApi<{}, {}>({
    url: API_URLS.LOGOUT,
    onSuccess: () => {
      // Note that navigation to login page is handled by auth guard (see AuthGuard.tsx)
      dispatch({
        type: Types.LOGOUT,
      });
    },
  });
  // #endregion

  const initialize = useCallback(async () => {
    try {
      const user = storageAvailable ? localStorage.getItem("user") : null;

      if (user) {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isLoggedIn: true,
            user: JSON.parse(user || ""),
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isLoggedIn: false,
            user: null,
          },
        });
      }
    } catch (error) {
      dispatch({
        type: Types.INITIAL,
        payload: {
          isLoggedIn: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    const response = await axiosInstance
      .post(API_URLS.LOGIN, {
        email,
        password,
      })
      .then(async (res) => {
        await axiosInstance.post(API_URLS.FCM_TOKEN, {
          fcm_token: localStorage.getItem("fcm_token"),
        });

        navigate(PATH_AFTER_LOGIN);
        return res;
      });

    // @ts-ignore
    const { user, csrf_token } = response;
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("csrf_token", csrf_token);

    dispatch({
      type: Types.LOGIN,
      payload: {
        user,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      const response = await axiosInstance
        .post("/signup", {
          email,
          password,
          first_name: firstName,
          last_name: lastName,
        })
        .then((res) => {
          navigate(PATH_AFTER_LOGIN);
          return res;
        });
      const { token, user } = response.data.data;

      localStorage.setItem("accessToken", token);

      dispatch({
        type: Types.REGISTER,
        payload: {
          user,
        },
      });
    },
    []
  );

  // LOGOUT
  const logout = useCallback(() => {
    mutateLogout({});
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isLoggedIn: !!state.user,
      user: state.user,
      method: "jwt",
      login,
      register,
      logout,
      isLoggingOut,
    }),
    [state.isLoggedIn, state.isInitialized, state.user, login, logout, register, isLoggingOut]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
